import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiCommand, FiArrowRight, FiActivity, FiBriefcase, FiUsers, FiDollarSign } from 'react-icons/fi';

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const toggle = useCallback(() => setIsOpen(open => !open), []);

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, [toggle]);

  const actions = [
    { icon: FiActivity, label: 'View Analytics', href: '/dashboard/admin', shortcut: 'G A' },
    { icon: FiBriefcase, label: 'Manage Campaigns', href: '/dashboard/admin/campaigns', shortcut: 'G C' },
    { icon: FiUsers, label: 'User Management', href: '/dashboard/admin/users', shortcut: 'G U' },
    { icon: FiDollarSign, label: 'Financial Reports', href: '/dashboard/admin/payouts', shortcut: 'G F' },
  ].filter(a => a.label.toLowerCase().includes(query.toLowerCase()));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
        >
          <div className="flex items-center px-4 py-4 border-b dark:border-slate-800">
            <FiSearch className="w-5 h-5 text-slate-400 mr-3" />
            <input
              autoFocus
              className="flex-1 bg-transparent border-none outline-none text-slate-900 dark:text-white placeholder:text-slate-400"
              placeholder="Search actions, pages, or entities..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-bold text-slate-500">
              <FiCommand className="w-3 h-3" /> K
            </div>
          </div>
          <div className="p-2 max-h-[60vh] overflow-y-auto">
            {actions.map((action, i) => (
              <button
                key={i}
                onClick={() => {
                  navigate(action.href);
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                    <action.icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{action.label}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[10px] text-slate-400 font-mono">{action.shortcut}</span>
                  <FiArrowRight className="w-3 h-3 text-slate-300 group-hover:text-primary-500 transition-transform group-hover:translate-x-1" />
                </div>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}